/**
 * AI generic-product mapping for import rows (DEV-3605).
 *
 * Catalogue is Boverket / CO2data.fi products that the Imports picker can
 * select. The model may only return ids from this list; conversion factors
 * are accepted only when native {@link candidateConversion} does not already
 * cover the row unit.
 */

import { genericProductsLookup } from '@shared/generic-products';
import {
  candidateConversion,
  candidateQuantityFrom,
  candidateSbef,
  candidateUnitFrom,
  resolveProductQuantityName,
} from './materialize-candidate.helpers';
import { candidateFieldValue } from './candidate-row-cells.helpers';
import { categorySelectableProducts } from './category-selectable-products.helpers';
import { isBoverketProductId, isCo2DataFiProductId } from './product.helpers';
import { getObjectHash, sortObject } from './object.helpers';
import { assertUnreachable } from './function.helpers';
import { toLookup, tryCatch } from './utils.helpers';
import { aiImportMappingOutputSchema } from './ai-import-mapping-json-schema.helpers';
import {
  rowWithActiveIfcMeasure,
  type IfcMeasure,
} from './ifc-quantity.helpers';
import type {
  CandidateRow,
  ImportRowDraft,
} from '@shared/models/element-import.model';
import type { IProduct } from '@shared/models/product.model';
import type {
  AiImportMappingCatalogItem,
  AiImportMappingFactor,
  AiImportMappingProductSuggestion,
  AiImportMappingResponse,
  AiImportMappingRowPayload,
  AiImportMappingSource,
  AiImportMappingSuggestion,
  AiImportMappingUnsupportedReason,
} from '@shared/models/ai-import-mapping.model';
import { AI_IMPORT_MAPPING_UNSUPPORTED_REASONS } from '@shared/models/ai-import-mapping.model';
import type { ConversionFactorUnit } from '@shared/models/unit.model';
import { conversionFactorQuantityUnits } from '@shared/models/unit.model';
import { isOneOf } from './array.helpers';
import { clamp } from './math.helpers';
import { guards } from './typeguards.helpers';

const CATALOG_FACTOR_UNITS: readonly ConversionFactorUnit[] = [
  'kg',
  'm³',
  'm²',
  'm',
  'pcs',
  'kWh',
];

const MAX_SUGGESTIONS = 3;
const MAX_EXTRA_FIELD_CHARS = 200;
const INSUFFICIENT_RATIONALE =
  'No matching generic product could be validated from the catalogue.';

const compactConversionFactors = (
  factors: IProduct['conversion_factors'],
): Partial<Record<ConversionFactorUnit, number>> => {
  const compact: Partial<Record<ConversionFactorUnit, number>> = {};
  for (const unit of CATALOG_FACTOR_UNITS) {
    const value = factors[unit];
    if (typeof value === 'number' && Number.isFinite(value)) {
      compact[unit] = value;
    }
  }
  return compact;
};

const toCatalogItem = (product: IProduct): AiImportMappingCatalogItem => ({
  id: product.id,
  name: product.name,
  source: product.source,
  unit: product.unit,
  conversion_factors: compactConversionFactors(product.conversion_factors),
});

const isEligibleAiImportMappingProduct = (product: IProduct): boolean =>
  isBoverketProductId(product) || isCo2DataFiProductId(product);

/** Selectable Boverket / CO2data.fi products the model may return. */
export const aiImportMappingCatalog: AiImportMappingCatalogItem[] =
  categorySelectableProducts
    .filter(isEligibleAiImportMappingProduct)
    .map(toCatalogItem);

export const aiImportMappingCatalogById = toLookup(aiImportMappingCatalog);

const extraFieldsFromRow = (row: CandidateRow): Record<string, string> => {
  const skip = new Set(
    [
      candidateFieldValue(row, 'name'),
      candidateFieldValue(row, 'code'),
      candidateFieldValue(row, 'unit'),
      candidateFieldValue(row, 'quantity'),
      candidateFieldValue(row, 'sbef'),
      row.name,
      row.resourceCode,
    ].filter((v): v is string => guards.is.string(v) && v.length > 0),
  );

  const extra: Record<string, string> = {};
  for (const [header, value] of Object.entries(row.raw)) {
    const text = String(value ?? '').trim();
    if (text.length === 0) continue;
    if (skip.has(text)) continue;
    extra[header] = text.slice(0, MAX_EXTRA_FIELD_CHARS);
  }
  return extra;
};

/**
 * Stable hash of the row fields the suggestion was computed from.
 * Approving must no-op when this no longer matches the live row.
 */
export const getAiImportMappingFingerprint = (row: CandidateRow): string =>
  getObjectHash(
    sortObject({
      id: row.id,
      name: row.name,
      resourceCode: row.resourceCode,
      quantity: candidateQuantityFrom(row) ?? null,
      unit: candidateUnitFrom(row) ?? null,
      sbef: candidateSbef(row) ?? null,
      raw: row.raw,
    }),
  );

/** Resource payload sent to the model together with the catalogue. */
export const buildAiImportMappingRowPayload = (
  row: CandidateRow,
): AiImportMappingRowPayload => ({
  rowId: row.id,
  fingerprint: getAiImportMappingFingerprint(row),
  name: row.name,
  resourceCode: row.resourceCode,
  quantity: candidateQuantityFrom(row),
  unit: candidateUnitFrom(row),
  sbef: candidateSbef(row),
  extraFields: extraFieldsFromRow(row),
});

const sanitizeSources = (
  sources: readonly { title: string; url: string | null }[],
): AiImportMappingSource[] =>
  sources
    .map((source) => {
      const title = source.title.trim();
      if (title.length === 0) return undefined;
      const url = source.url?.trim() ?? '';
      return {
        title,
        url: url.length > 0 ? url : null,
      };
    })
    .filter((source): source is AiImportMappingSource => source !== undefined);

const isUnsupportedReason = (
  value: string,
): value is AiImportMappingUnsupportedReason =>
  isOneOf(AI_IMPORT_MAPPING_UNSUPPORTED_REASONS, value);

const sanitizeFactor = (
  factor: {
    quantityFactor: number;
    unitTo: string;
    confidence: number;
    sources: readonly { title: string; url: string | null }[];
  } | null,
): AiImportMappingFactor | null => {
  if (!factor) return null;
  if (!Number.isFinite(factor.quantityFactor) || factor.quantityFactor <= 0) {
    return null;
  }
  if (!isOneOf(conversionFactorQuantityUnits, factor.unitTo)) return null;
  const sources = sanitizeSources(factor.sources);
  if (sources.length === 0) return null;
  const confidence = Number.isFinite(factor.confidence)
    ? clamp(factor.confidence, 0, 1)
    : 0;
  return {
    quantityFactor: factor.quantityFactor,
    unitTo: factor.unitTo,
    confidence,
    sources,
  };
};

const clampConfidence = (value: number | null): number => {
  if (value === null || !Number.isFinite(value)) return 0;
  return clamp(value, 0, 1);
};

const toUnsupported = (
  reason: AiImportMappingUnsupportedReason,
  rationale: string,
): AiImportMappingSuggestion => ({
  kind: 'unsupported',
  reason,
  rationale: rationale.trim() || INSUFFICIENT_RATIONALE,
});

const resolveNativeConversion = (
  row: CandidateRow,
  product: IProduct,
): boolean => Boolean(candidateConversion(row, product));

/**
 * Turn a parsed model item into a catalogue-validated suggestion.
 * Invented product ids are dropped. A native product conversion replaces any
 * model-supplied factor.
 */
const normalizeSuggestion = (
  item: {
    productId: string | null;
    productConfidence: number | null;
    factor: {
      quantityFactor: number;
      unitTo: string;
      confidence: number;
      sources: { title: string; url: string | null }[];
    } | null;
    rationale: string;
    sources: { title: string; url: string | null }[];
    unsupportedReason: string | null;
  },
  row: CandidateRow,
): AiImportMappingSuggestion | undefined => {
  if (item.unsupportedReason && isUnsupportedReason(item.unsupportedReason)) {
    return toUnsupported(item.unsupportedReason, item.rationale);
  }

  const productId = item.productId?.trim() ?? '';
  if (productId.length === 0) {
    return toUnsupported('insufficient_evidence', item.rationale);
  }
  if (!aiImportMappingCatalogById[productId]) return undefined;
  const product = genericProductsLookup[productId];
  if (!product) return undefined;

  const factor = resolveNativeConversion(row, product)
    ? null
    : sanitizeFactor(item.factor);

  return {
    kind: 'product',
    productId,
    productConfidence: clampConfidence(item.productConfidence),
    factor,
    rationale: item.rationale.trim(),
    sources: sanitizeSources(item.sources),
  };
};

const insufficientSuggestion = (): AiImportMappingSuggestion =>
  toUnsupported('insufficient_evidence', INSUFFICIENT_RATIONALE);

/**
 * Parse and validate a model JSON payload into the public suggestion shape.
 * Unknown product ids are dropped; an empty result becomes a single
 * `insufficient_evidence` suggestion.
 */
export const buildAiImportMappingResponse = (
  row: CandidateRow,
  raw: unknown,
): AiImportMappingResponse => {
  const parsed = tryCatch(() => aiImportMappingOutputSchema.parse(raw));
  const fingerprint = getAiImportMappingFingerprint(row);
  if (!parsed.ok) {
    return {
      rowId: row.id,
      fingerprint,
      suggestions: [insufficientSuggestion()],
    };
  }

  const suggestions: AiImportMappingSuggestion[] = [];
  for (const item of parsed.value.suggestions) {
    if (suggestions.length >= MAX_SUGGESTIONS) break;
    const normalized = normalizeSuggestion(item, row);
    if (normalized) suggestions.push(normalized);
  }

  return {
    rowId: row.id,
    fingerprint,
    suggestions:
      suggestions.length > 0 ? suggestions : [insufficientSuggestion()],
  };
};

/**
 * Draft patch that applies an approved product suggestion.
 * Does not mutate the generic product. Returns undefined when the suggestion
 * is not a product or the catalogue id is gone.
 */
export const buildImportRowDraftFromAiSuggestion = (
  row: CandidateRow,
  suggestion: AiImportMappingSuggestion,
  product: IProduct,
): Partial<ImportRowDraft> | undefined => {
  switch (suggestion.kind) {
    case 'unsupported':
      return undefined;
    case 'product': {
      if (suggestion.productId !== product.id) return undefined;
      if (!aiImportMappingCatalogById[product.id]) return undefined;
      const selectedQuantity = resolveProductQuantityName({ product, row });
      if (suggestion.factor && !resolveNativeConversion(row, product)) {
        return {
          referenceId: product.id,
          referenceType: 'product',
          selectedQuantity,
          quantityFactor: suggestion.factor.quantityFactor,
          unitTo: suggestion.factor.unitTo,
        };
      }
      return {
        referenceId: product.id,
        referenceType: 'product',
        selectedQuantity,
        quantityFactor: undefined,
        unitTo: undefined,
        totalQuantity: undefined,
        expression: undefined,
      };
    }
    default:
      return assertUnreachable(suggestion);
  }
};

/** True when the live row still matches the suggestion fingerprint. */
export const isAiImportMappingFingerprintCurrent = (
  row: CandidateRow,
  fingerprint: string,
): boolean => getAiImportMappingFingerprint(row) === fingerprint;

/**
 * First product suggestion in an AI response. Unsupported items are skipped so
 * applying without a review UI still maps a material product when one exists.
 */
export const getPrimaryAiImportMappingProductSuggestion = (
  suggestions: readonly AiImportMappingSuggestion[],
): AiImportMappingProductSuggestion | undefined => {
  for (const suggestion of suggestions) {
    switch (suggestion.kind) {
      case 'product':
        return suggestion;
      case 'unsupported':
        continue;
      default:
        return assertUnreachable(suggestion);
    }
  }
  return undefined;
};

export type AiImportMappingDraftResult =
  | { kind: 'draft'; draft: Partial<ImportRowDraft> }
  | { kind: 'stale' }
  | { kind: 'unsupported'; reason: AiImportMappingUnsupportedReason }
  | { kind: 'no_match' }
  | { kind: 'missing_product' }
  | { kind: 'no_draft' };

/**
 * Turn one suggestion response into a draft patch, or a reason it cannot be
 * applied. Uses {@link genericProductsLookup} for catalogue ids.
 */
export const resolveAiImportMappingDraft = (
  row: CandidateRow,
  ifcMeasure: IfcMeasure | undefined,
  response: AiImportMappingResponse,
): AiImportMappingDraftResult => {
  const effectiveRow = rowWithActiveIfcMeasure(row, ifcMeasure);
  if (
    !isAiImportMappingFingerprintCurrent(effectiveRow, response.fingerprint)
  ) {
    return { kind: 'stale' };
  }
  const suggestion = getPrimaryAiImportMappingProductSuggestion(
    response.suggestions,
  );
  if (!suggestion) {
    const first = response.suggestions[0];
    if (first?.kind === 'unsupported') {
      return { kind: 'unsupported', reason: first.reason };
    }
    return { kind: 'no_match' };
  }
  const product = genericProductsLookup[suggestion.productId];
  if (!product) return { kind: 'missing_product' };
  const draft = buildImportRowDraftFromAiSuggestion(
    effectiveRow,
    suggestion,
    product,
  );
  if (!draft) return { kind: 'no_draft' };
  return { kind: 'draft', draft };
};
